import { DispatchedReportItem, ReportScheduleItem } from '@futrli-sdk/report_scheduling';
import { WorkspaceMember } from '@futrli-sdk/people';

import { calculateEndDateFromOccurrences } from '../../utils/reports/calculateEndDateFromOccurrences';
import { getUserName } from '../../utils/users/getUserName';

export type ScheduleDerivedStatus = 'scheduled' | 'awaiting-review' | 'expired';

export type ScheduleRepeatKey =
  | 'daily'
  | 'monthly'
  | 'weekly'
  | 'does-not-repeat'
  | 'quarterly'
  | 'annually'
  | 'every-days'
  | 'every-weeks'
  | 'every-months';

const REPEATING_FREQUENCY_QUARTERLY = 3;
const REPEATING_FREQUENCY_ANNUALLY = 12;
type NonCustomScheduleRepeatKey = Exclude<ScheduleRepeatKey, 'every-days' | 'every-weeks' | 'every-months'>;
type RepeatingSchedulePeriod = Exclude<ReportScheduleItem['period'], 'do-not-repeat'>;
type CustomScheduleRepeatKey = Exclude<ScheduleRepeatKey, NonCustomScheduleRepeatKey>;

const REPEAT_KEYS_BY_PERIOD: Record<ReportScheduleItem['period'], NonCustomScheduleRepeatKey> = {
  'daily': 'daily',
  'monthly': 'monthly',
  'weekly': 'weekly',
  'do-not-repeat': 'does-not-repeat'
};

const CUSTOM_REPEAT_KEYS_BY_PERIOD: Record<RepeatingSchedulePeriod, CustomScheduleRepeatKey> = {
  daily: 'every-days',
  monthly: 'every-months',
  weekly: 'every-weeks'
};

export type ScheduleRepeat =
  | {
    readonly key: Exclude<ScheduleRepeatKey, 'every-days' | 'every-weeks' | 'every-months'>;
    readonly count?: never;
  }
  | {
    readonly key: 'every-days' | 'every-weeks' | 'every-months';
    readonly count: number;
  };

export interface ScheduleColumnData {
  readonly endDate: Date | null;
  readonly recipientCount: number | null;
  readonly repeat: ScheduleRepeat | null;
  readonly reviewRequired: boolean | null;
  readonly sendDate: Date | null;
}

interface ScheduleListItemBase {
  readonly name: string;
  readonly createdByName: string | null;
  readonly reportId: string | null;
  readonly derivedStatus: ScheduleDerivedStatus;
  readonly next_generate_at: string | null;
  readonly columns: ScheduleColumnData;
}

type ScheduleListSource =
  | {
    readonly source: 'schedule';
    readonly sourceItem: ReportScheduleItem;
  }
  | {
    readonly source: 'dispatched';
    readonly sourceItem: DispatchedReportItem;
  };

export type ScheduleListItem = ScheduleListItemBase & ScheduleListSource;

const getDate = (value: string | null | undefined): Date | null => (value ? new Date(value) : null);

const getScheduleEndDate = (schedule: ReportScheduleItem): Date | null => {
  if (schedule.end_type === 'end-date') {
    return getDate(schedule.end_date);
  }

  if (schedule.end_type === 'end-after-occurrences') {
    return calculateEndDateFromOccurrences(schedule);
  }

  return null;
};

const isScheduleExpired = (schedule: ReportScheduleItem): boolean => {
  const endDate = getScheduleEndDate(schedule);

  return endDate !== null && endDate < new Date();
};

const getRepeat = (period: ReportScheduleItem['period'], repeatingFrequency: number): ScheduleRepeat => {
  if (repeatingFrequency === 0 || period === 'do-not-repeat') {
    return { key: 'does-not-repeat' };
  }

  if (repeatingFrequency === 1) {
    return { key: REPEAT_KEYS_BY_PERIOD[period] };
  }

  if (repeatingFrequency === REPEATING_FREQUENCY_QUARTERLY && period === 'monthly') {
    return { key: 'quarterly' };
  }

  if (repeatingFrequency === REPEATING_FREQUENCY_ANNUALLY && period === 'monthly') {
    return { key: 'annually' };
  }

  return {
    key: CUSTOM_REPEAT_KEYS_BY_PERIOD[period],
    count: repeatingFrequency
  };
};

const getEndDate = (source: ScheduleListSource): Date | null => {
  if (source.source === 'schedule') {
    return getScheduleEndDate(source.sourceItem);
  }

  if (source.sourceItem.end_type === 'end-date') {
    return getDate(source.sourceItem.end_date);
  }

  return null;
};

const getSendDate = (source: ScheduleListSource): Date | null => {
  const dateValue = source.sourceItem.next_generate_at || source.sourceItem.end_date;

  if (dateValue) {
    return new Date(dateValue);
  }

  if (source.source === 'schedule') {
    return new Date(source.sourceItem.start_date);
  }

  return null;
};

const getReviewRequired = (source: ScheduleListSource): boolean =>
  source.source === 'dispatched' || source.sourceItem.approval_email_user;

const getScheduleColumns = (source: ScheduleListSource, derivedStatus: ScheduleDerivedStatus): ScheduleColumnData => {
  const endDate = getEndDate(source);

  if (derivedStatus === 'expired') {
    return {
      endDate,
      recipientCount: null,
      repeat: null,
      reviewRequired: null,
      sendDate: null
    };
  }

  return {
    endDate,
    recipientCount: source.sourceItem.users.length,
    repeat: getRepeat(source.sourceItem.period, source.sourceItem.repeating_frequency),
    reviewRequired: getReviewRequired(source),
    sendDate: getSendDate(source)
  };
};

const getCreatedByName = (
  source: ScheduleListSource,
  membersById: Map<string, WorkspaceMember>
): string | null => {
  const member = membersById.get(source.sourceItem.created_by);

  return member?.name ? getUserName(member.name) : null;
};

const createListItem = (
  source: ScheduleListSource,
  name: string,
  reportId: string | null,
  derivedStatus: ScheduleDerivedStatus,
  membersById: Map<string, WorkspaceMember>
): ScheduleListItem => ({
  name,
  createdByName: getCreatedByName(source, membersById),
  reportId,
  derivedStatus,
  next_generate_at: source.sourceItem.next_generate_at,
  columns: getScheduleColumns(source, derivedStatus),
  ...source
});

/**
 * Merges schedules and awaiting-review reports into the domain list consumed by
 * the report scheduling screen.
 */
export const deriveScheduleList = (
  scheduledReports: ReportScheduleItem[],
  awaitingReviewReports: DispatchedReportItem[],
  members: WorkspaceMember[] = []
): ScheduleListItem[] => {
  const items: ScheduleListItem[] = [];
  const scheduleById = new Map(scheduledReports.map(schedule => [schedule.id, schedule]));
  const membersById = new Map<string, WorkspaceMember>(members.map(member => [member.id, member] as const));

  for (const schedule of scheduledReports) {
    const derivedStatus = isScheduleExpired(schedule) ? 'expired' : 'scheduled';

    items.push(
      createListItem(
        { source: 'schedule', sourceItem: schedule },
        schedule.name,
        schedule.report,
        derivedStatus,
        membersById
      )
    );
  }

  for (const dispatched of awaitingReviewReports) {
    if (dispatched.status !== 'awaiting-permission') {
      continue;
    }

    const parentSchedule = scheduleById.get(dispatched.report_schedule_id);

    items.push(
      createListItem(
        { source: 'dispatched', sourceItem: dispatched },
        dispatched.name,
        parentSchedule?.report ?? null,
        'awaiting-review',
        membersById
      )
    );
  }

  return items;
};
